// ═══════════════════════════════════════════════════════════════════════════
// DOCUMENTI DEI SUBAPPALTATORI — regole pure (ottobre 2026, piano Gold)
// ═══════════════════════════════════════════════════════════════════════════
// Idoneità tecnico-professionale di imprese subappaltatrici e dei loro lavoratori:
// catalogo dei documenti (modificabile da ogni azienda), stati, scadenze proposte,
// raggruppamento delle richieste via email (una sola email per subappaltatore).
// Nessun accesso a Firebase qui: tutto è provabile con dati finti.
//
// Decisioni (Paolo, ott 2026): richiesta 30 giorni prima della scadenza, un sollecito
// 7 giorni prima ("se già inviati, ignorate"); file delle versioni superate cancellati
// dopo 60 giorni, registro sempre conservato. Tutti valori modificabili per azienda.
// ═══════════════════════════════════════════════════════════════════════════

export const LIVELLI = {
  azienda: { id: "azienda", label: "Azienda" },
  lavoratore: { id: "lavoratore", label: "Lavoratori" },
  commessa: { id: "commessa", label: "Commesse" },
};

export const IMPOSTAZIONI_PREDEFINITE = {
  giorniPrimaRichiesta: 30,
  giorniSollecito: 7,
  giorniConservazioneFile: 60,
  emailRisposte: "",          // casella dell'azienda dove il subappaltatore risponde
  richiesteAutomatiche: true,
};

// validita: { giorni } | { mesi } | null (scadenza da indicare a mano o nessuna scadenza)
// sanitario: documento RISERVATO (dati sanitari o di familiari) → visibile solo a chi verifica
const T = (id, livello, label, validita = null, extra = {}) => ({ id, livello, label, validita, obbligatorio: true, attivo: true, ...extra });

export const CATALOGO_PREDEFINITO = [
  // Azienda
  T("durc", "azienda", "DURC", { giorni: 120 }),
  T("visura", "azienda", "Visura camerale", { mesi: 6 }, { nota: "Di solito si chiede una visura non più vecchia di 6 mesi" }),
  T("polizza_rc", "azienda", "Polizza RCT/RCO", null, { nota: "Scadenza riportata sulla polizza" }),
  T("dvr", "azienda", "DVR o autocertificazione della valutazione dei rischi"),
  T("nomina_rspp", "azienda", "Nomina RSPP"),
  T("nomina_mc", "azienda", "Nomina del medico competente"),
  T("nomina_rls", "azienda", "Nomina RLS o dichiarazione"),
  T("organico_ccnl", "azienda", "Dichiarazione organico medio e CCNL applicato", { mesi: 12 }),
  T("non_sospensione", "azienda", "Dichiarazione di assenza di provvedimenti di sospensione", { mesi: 12 }),
  T("white_list", "azienda", "Iscrizione White List", { mesi: 12 }, { obbligatorio: false }),
  T("certificazioni", "azienda", "Certificazioni (SOA, F-Gas, altre)", null, { obbligatorio: false }),
  // "sanitario" qui significa RISERVATO: visibile solo a chi verifica (dati di familiari)
  T("antimafia", "azienda", "Documentazione antimafia (dichiarazioni familiari conviventi)", { mesi: 6 }, { obbligatorio: false, sanitario: true, nota: "Dati riservati di familiari: visibili solo a chi verifica. Rendila obbligatoria se lavori con enti pubblici" }),
  // Lavoratori
  T("identita", "lavoratore", "Documento d'identità", null, { nota: "Scadenza riportata sul documento" }),
  T("tesserino", "lavoratore", "Tesserino di riconoscimento"),
  T("unilav", "lavoratore", "UNILAV / comunicazione di assunzione"),
  T("patente", "lavoratore", "Patente (ed eventuale CQC)", null, { obbligatorio: false, nota: "Scadenza riportata sulla patente" }),
  T("formazione_generale", "lavoratore", "Formazione generale sicurezza"),
  T("formazione_specifica", "lavoratore", "Formazione specifica sicurezza", { mesi: 60 }),
  T("pes_pav", "lavoratore", "PES / PAV / PEI", { mesi: 60 }, { obbligatorio: false }),
  T("quota", "lavoratore", "Lavori in quota e DPI anticaduta", { mesi: 60 }, { obbligatorio: false }),
  T("ple", "lavoratore", "Piattaforme di lavoro elevabili (PLE)", { mesi: 60 }, { obbligatorio: false }),
  T("ponteggi", "lavoratore", "Montaggio ponteggi (PiMUS)", { mesi: 48 }, { obbligatorio: false }),
  T("attrezzature", "lavoratore", "Carrelli, gru e altre attrezzature", { mesi: 60 }, { obbligatorio: false }),
  T("spazi_confinati", "lavoratore", "Spazi confinati", { mesi: 60 }, { obbligatorio: false }),
  T("primo_soccorso", "lavoratore", "Primo soccorso", { mesi: 36 }, { obbligatorio: false }),
  T("antincendio", "lavoratore", "Antincendio", { mesi: 60 }, { obbligatorio: false }),
  T("preposto", "lavoratore", "Preposto", { mesi: 24 }, { obbligatorio: false }),
  T("idoneita", "lavoratore", "Idoneità alla mansione (medico competente)", null, { sanitario: true, nota: "Scadenza indicata dal medico competente" }),
  T("consegna_dpi", "lavoratore", "Consegna DPI"),
  // Commessa
  T("pos", "commessa", "POS — Piano operativo di sicurezza"),
  T("accettazione_pos", "commessa", "Verbale di consegna / accettazione del POS"),
  T("contratto_subappalto", "commessa", "Contratto o ordine di subappalto"),
  T("autorizzazione_subappalto", "commessa", "Autorizzazione al subappalto (lavori pubblici)", null, { obbligatorio: false }),
];

/** Catalogo dell'azienda: i predefiniti con le modifiche salvate, più i tipi aggiunti. */
export function catalogoAzienda(modifiche = {}, aggiunti = []) {
  const base = CATALOGO_PREDEFINITO.map(t => ({ ...t, ...(modifiche[t.id] || {}) }));
  return [...base, ...aggiunti.map(t => ({ attivo: true, obbligatorio: true, validita: null, ...t, personalizzato: true }))];
}

const p2 = (n) => String(n).padStart(2, "0");
export const dataIso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const daIso = (s) => { const [a, m, g] = String(s).slice(0, 10).split("-").map(Number); return new Date(a, m - 1, g); };

/** Scadenza proposta dalla data del documento e dalla validità del tipo ("" se non calcolabile). */
export function scadenzaProposta(dataDocumento, validita) {
  if (!dataDocumento || !validita) return "";
  const d = daIso(dataDocumento);
  if (validita.giorni) d.setDate(d.getDate() + validita.giorni);
  else if (validita.mesi) {
    const giorno = d.getDate(); d.setDate(1); d.setMonth(d.getMonth() + validita.mesi);
    const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(giorno, ultimo));
  }
  return dataIso(d);
}

export function giorniAllaScadenza(scadenza, oggi = new Date()) {
  if (!scadenza) return null;
  const o = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate());
  return Math.round((daIso(scadenza) - o) / 86400000);
}

export const STATI = {
  mancante:     { id: "mancante",     label: "Mancante",       colore: "#dc2626", bg: "#fee2e2", gravita: 3 },
  scaduto:      { id: "scaduto",      label: "Scaduto",        colore: "#dc2626", bg: "#fee2e2", gravita: 3 },
  rifiutato:    { id: "rifiutato",    label: "Rifiutato",      colore: "#b91c1c", bg: "#fef2f2", gravita: 3 },
  daVerificare: { id: "daVerificare", label: "Da verificare",  colore: "#2563eb", bg: "#dbeafe", gravita: 2 },
  inScadenza:   { id: "inScadenza",   label: "In scadenza",    colore: "#d97706", bg: "#fef3c7", gravita: 1 },
  valido:       { id: "valido",       label: "Valido",         colore: "#059669", bg: "#d1fae5", gravita: 0 },
};

/**
 * Stato di un documento richiesto.
 * doc: { stato: "valido"|"daVerificare"|"rifiutato", dataScadenza } oppure null se mai caricato.
 */
export function statoDocumento(doc, { oggi = new Date(), giorniPreavviso = IMPOSTAZIONI_PREDEFINITE.giorniPrimaRichiesta } = {}) {
  if (!doc) return STATI.mancante;
  if (doc.stato === "daVerificare") return STATI.daVerificare;
  if (doc.stato === "rifiutato") return STATI.rifiutato;
  const g = giorniAllaScadenza(doc.dataScadenza, oggi);
  if (g === null) return STATI.valido;          // documento senza scadenza
  if (g < 0) return STATI.scaduto;
  if (g <= giorniPreavviso) return STATI.inScadenza;
  return STATI.valido;
}

/** Semaforo complessivo: 🔴 se c'è qualcosa di grave, 🟡 se c'è qualcosa da seguire, 🟢 altrimenti. */
export function semaforo(stati) {
  const max = Math.max(0, ...stati.map(s => s.gravita));
  if (max >= 3) return { colore: "rosso", emoji: "🔴", label: "Documenti scaduti o mancanti" };
  if (max >= 1) return { colore: "giallo", emoji: "🟡", label: "Documenti in scadenza o da verificare" };
  return { colore: "verde", emoji: "🟢", label: "Tutto in regola" };
}

/**
 * Righe dei documenti richiesti per un subappaltatore: una per ogni tipo attivo e
 * obbligatorio del livello giusto (azienda; per ogni lavoratore; per ogni commessa
 * assegnata), con il documento corrente se esiste.
 * documenti: elenco dei documenti CORRENTI (non le versioni superate).
 */
export function righeRichieste({ subappaltatore, catalogo, documenti, oggi = new Date(), giorniPreavviso }) {
  const attivi = catalogo.filter(t => t.attivo !== false);
  const cerca = (tipoId, rif) => documenti.find(d => d.tipoId === tipoId && (d.lavoratoreId || "") === (rif.lavoratoreId || "") && (d.projectId || "") === (rif.projectId || ""));
  const righe = [];
  const aggiungi = (t, rif, soggetto) => {
    const doc = cerca(t.id, rif);
    if (!doc && !t.obbligatorio) return;    // facoltativo mai caricato: non è un'assenza
    righe.push({ tipo: t, ...rif, soggetto, doc: doc || null, stato: statoDocumento(doc, { oggi, giorniPreavviso }) });
  };
  attivi.filter(t => t.livello === "azienda").forEach(t => aggiungi(t, {}, subappaltatore.ragioneSociale || ""));
  (subappaltatore.lavoratori || []).filter(l => l.attivo !== false).forEach(l =>
    attivi.filter(t => t.livello === "lavoratore").forEach(t => aggiungi(t, { lavoratoreId: l.id }, `${l.nome || ""} ${l.cognome || ""}`.trim())));
  (subappaltatore.commesse || []).forEach(c =>
    attivi.filter(t => t.livello === "commessa").forEach(t => aggiungi(t, { projectId: c.projectId }, c.nome || "")));
  return righe;
}

/**
 * Cosa chiedere oggi via email a un subappaltatore. Una sola email con tutto:
 *  - "richiesta" se qualche documento entra oggi nella finestra dei 30 giorni o è già scaduto/mancante
 *    e non è stato ancora richiesto;
 *  - "sollecito" se qualche documento è a 7 giorni o meno e il sollecito non è ancora partito.
 * ultimeRichieste: { [chiaveRiga]: { richiestaIl, sollecitoIl } }
 */
export function chiaveRiga(r) { return [r.tipo.id, r.lavoratoreId || "", r.projectId || ""].join("|"); }

export function daRichiedere(righe, { oggi = new Date(), impostazioni = IMPOSTAZIONI_PREDEFINITE, ultimeRichieste = {} } = {}) {
  const richiesta = [], sollecito = [];
  righe.forEach(r => {
    if (["daVerificare"].includes(r.stato.id)) return;         // già arrivato, in verifica
    const k = chiaveRiga(r); const storia = ultimeRichieste[k] || {};
    const scad = r.doc?.dataScadenza;
    const g = giorniAllaScadenza(scad, oggi);
    const versione = scad || "senza-scadenza";
    const giaRichiesto = storia.perScadenza === versione && storia.richiestaIl;
    const giaSollecitato = storia.perScadenza === versione && storia.sollecitoIl;
    const daChiedere = r.stato.id === "mancante" || r.stato.id === "rifiutato" || (g !== null && g <= impostazioni.giorniPrimaRichiesta);
    if (!daChiedere) return;
    if (!giaRichiesto) { richiesta.push(r); return; }
    if (g !== null && g <= impostazioni.giorniSollecito && !giaSollecitato) sollecito.push(r);
  });
  return { richiesta, sollecito, tipo: richiesta.length ? "richiesta" : (sollecito.length ? "sollecito" : null) };
}

/** Testo dell'email (oggetto + righe), uguale nell'anteprima dell'app e nella funzione che la invia. */
export function testoEmailRichiesta({ azienda, subappaltatore, righe, tipo, emailRisposte, fmtData = (s) => s ? s.split("-").reverse().join("/") : "" }) {
  const oggetto = tipo === "sollecito"
    ? `Sollecito: documenti in scadenza — ${azienda}`
    : `Richiesta documenti per l'idoneità tecnico-professionale — ${azienda}`;
  const elenco = righe.map(r => {
    const chi = r.lavoratoreId || r.projectId ? ` — ${r.soggetto}` : "";
    const quando = r.stato.id === "mancante" ? "mancante" : r.stato.id === "rifiutato" ? `da sostituire${r.doc?.motivoRifiuto ? ` (${r.doc.motivoRifiuto})` : ""}`
      : r.doc?.dataScadenza ? `${r.stato.id === "scaduto" ? "scaduto il" : "scade il"} ${fmtData(r.doc.dataScadenza)}` : "da aggiornare";
    return `• ${r.tipo.label}${chi}: ${quando}`;
  });
  const apertura = tipo === "sollecito"
    ? "vi ricordiamo che alcuni documenti richiesti nelle scorse settimane sono in scadenza o non ci sono ancora pervenuti:"
    : "per mantenere aggiornata la documentazione di idoneità tecnico-professionale vi chiediamo di inviarci i seguenti documenti:";
  const corpo = [
    `Gentile ${subappaltatore.referente || subappaltatore.ragioneSociale || ""},`.trim(),
    "",
    apertura,
    "",
    ...elenco,
    "",
    `Potete rispondere a questa email allegando i documenti${emailRisposte ? ` (oppure inviarli a ${emailRisposte})` : ""}.`,
    ...(tipo === "sollecito" ? ["", "Se li avete già inviati, potete ignorare questo messaggio."] : []),
    "",
    "Grazie per la collaborazione.",
    azienda,
    "",
    "—",
    `I documenti sono trattati da ${azienda} per le verifiche previste dalla normativa sulla sicurezza nei cantieri.`,
  ];
  return { oggetto, corpo: corpo.join("\n") };
}

/** Versioni superate il cui file va cancellato (il registro resta sempre). */
export function fileDaCancellare(versioni, { oggi = new Date(), giorni = IMPOSTAZIONI_PREDEFINITE.giorniConservazioneFile } = {}) {
  return versioni.filter(v => v.superatoIl && v.filePath && !v.fileCancellato && giorniAllaScadenza(v.superatoIl, oggi) <= -giorni);
}

/**
 * Idoneità delle ditte (W413): per le ditte di cui l'azienda gestisce già i documenti
 * (almeno un documento registrato), il semaforo e i documenti che non vanno bene.
 * Chi non verifica legge solo i documenti non sanitari: si escludono anche i tipi sanitari
 * dal catalogo, così non risultano «mancanti».
 * → Map(id ditta → { semaforo, problemi: [{ label, stato, dataScadenza, soggetto }] })
 */
export function idoneitaDitte({ ditte = [], documenti = [], impostazioniDoc = {}, oggi = new Date(), soloNonSanitari = true } = {}) {
  const impostazioni = { ...IMPOSTAZIONI_PREDEFINITE, ...(impostazioniDoc?.impostazioni || {}) };
  const catalogo = catalogoAzienda(impostazioniDoc?.modifiche || {}, impostazioniDoc?.aggiunti || []).filter(t => !soloNonSanitari || !t.sanitario);
  const correnti = documenti.filter(d => d.corrente !== false);
  const out = new Map();
  ditte.forEach(s => {
    const suoi = correnti.filter(d => d.subappaltatoreId === s.id);
    if (!suoi.length) return;   // documenti non gestiti per questa ditta: niente semaforo
    const righe = righeRichieste({ subappaltatore: s, catalogo, documenti: suoi, oggi, giorniPreavviso: impostazioni.giorniPrimaRichiesta });
    const problemi = righe.filter(r => r.stato.gravita >= 3)
      .map(r => ({ label: r.tipo.label || "Documento", stato: r.stato.label, dataScadenza: r.doc?.dataScadenza || "", soggetto: r.lavoratoreId ? r.soggetto : "" }));
    out.set(s.id, { semaforo: semaforo(righe.map(r => r.stato)), problemi });
  });
  return out;
}

/** «DURC scaduto il 12/10/2026, Visura camerale mancante» */
export function testoProblemiDitta(problemi = [], max = 3) {
  const f = (s) => (s ? s.split("-").reverse().join("/") : "");
  const t = problemi.slice(0, max).map(p => `${p.label}${p.soggetto ? ` di ${p.soggetto}` : ""} ${p.stato.toLowerCase()}${p.stato === "Scaduto" && p.dataScadenza ? ` il ${f(p.dataScadenza)}` : ""}`.trim());
  return t.join(", ") + (problemi.length > max ? ` e altri ${problemi.length - max}` : "");
}
